import { useRef, useState, useMemo, Suspense, Component, useEffect, type ReactNode } from "react";
import { Canvas, useFrame, type ThreeEvent } from "@react-three/fiber";
import { useGLTF, Center, Html, Line } from "@react-three/drei";
import { gltfWithAuth } from "../scene/gltfAuth";
import * as THREE from "three";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, uploadModel, getEdgeConfigDefault, setEdgeConfigDefault, type AssetType, type Asset } from "../api/rest";
import { useSiteId } from "../hooks/useSiteId";
import { useWarehouseId } from "../hooks/useWarehouseId";
import css from "./AssetsAdminPage.module.css";

// ── 3D preview helpers ─────────────────────────────────────────────────────

class ModelErrorBoundary extends Component<
  { children: ReactNode; unavailableText: string },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  override render() {
    if (this.state.failed)
      return <div className={css.previewError}>{this.props.unavailableText}</div>;
    return this.props.children;
  }
}

// Renders the model, handles spinning (normal mode) and point picking (calibration).
// effectiveScale is pre-computed by the dialog; e.point from clicks is world-space
// (with that scale applied), so the dialog divides worldDist by calibStartScale.
function ModelViewer({ url, calibrating, calibPts, onPointPick, effectiveScale }: {
  url: string;
  calibrating: boolean;
  calibPts: THREE.Vector3[];
  onPointPick: (p: THREE.Vector3) => void;
  effectiveScale: number;
}) {
  const { scene } = useGLTF(url, true, true, gltfWithAuth);
  const spinRef = useRef<THREE.Group>(null);
  const clone   = useMemo(() => scene.clone(true), [scene]);

  useFrame((_, dt) => {
    if (!calibrating && spinRef.current) spinRef.current.rotation.y += dt * 0.6;
  });

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    if (!calibrating || calibPts.length >= 2) return;
    e.stopPropagation();
    onPointPick(e.point.clone());
  };

  return (
    <>
      <group scale={effectiveScale}>
        <group ref={spinRef} onClick={handleClick}>
          <Center><primitive object={clone} /></Center>
        </group>
      </group>
      {/* Markers and line are in world space — outside the scale group */}
      {calibPts.map((p, i) => (
        <Html key={i} position={[p.x, p.y, p.z]} center zIndexRange={[10, 0]}>
          <div style={{
            width: 14, height: 14, borderRadius: "50%",
            background: i === 0 ? "#4ade80" : "#f87171",
            border: "2px solid #fff",
            boxShadow: "0 0 6px rgba(0,0,0,.6)",
            pointerEvents: "none",
          }} />
        </Html>
      ))}
      {calibPts.length === 2 && (
        <Line
          points={[calibPts[0].toArray() as [number,number,number], calibPts[1].toArray() as [number,number,number]]}
          color="#fb923c" lineWidth={2}
        />
      )}
    </>
  );
}

function ModelPreview({ url, unavailableText, calibrating, calibPts, onPointPick, effectiveScale }: {
  url: string; unavailableText: string;
  calibrating: boolean; calibPts: THREE.Vector3[];
  onPointPick: (p: THREE.Vector3) => void; effectiveScale: number;
}) {
  return (
    <ModelErrorBoundary unavailableText={unavailableText}>
      <div className={`${css.preview}${calibrating ? " " + css.previewCalibrating : ""}`}>
        <Canvas camera={{ position: [0, 1.5, 3], fov: 45 }}>
          <ambientLight intensity={0.8} />
          <directionalLight position={[2, 3, 2]} intensity={1} />
          <Suspense fallback={null}>
            <ModelViewer
              url={url}
              calibrating={calibrating}
              calibPts={calibPts}
              onPointPick={onPointPick}
              effectiveScale={effectiveScale}
            />
          </Suspense>
        </Canvas>
      </div>
    </ModelErrorBoundary>
  );
}

// ── Model dialog ───────────────────────────────────────────────────────────

function ModelDialog({ type, siteId, onClose, onUploaded }: {
  type: AssetType;
  siteId: string;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  // Upload state
  const [progress, setProgress]   = useState(0);
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "done" | "error">("idle");
  const [uploadError, setUploadError]   = useState("");

  // Calibration state
  const [calibrating, setCalibrating]     = useState(false);
  const [calibStartScale, setCalibStartScale] = useState(1);
  const [calibPts, setCalibPts]           = useState<THREE.Vector3[]>([]);
  const [realDist, setRealDist]           = useState("");
  const [calibSaved, setCalibSaved]       = useState(false);

  // The scale used to display the model (live-updates as user types during calib)
  const worldDist    = calibPts.length === 2 ? calibPts[0].distanceTo(calibPts[1]) : 0;
  const parsedDist   = parseFloat(realDist);
  // worldDist was measured at calibStartScale, so modelDist = worldDist / calibStartScale
  const computedScale = worldDist > 0 && parsedDist > 0 && isFinite(parsedDist)
    ? parsedDist * calibStartScale / worldDist
    : null;

  const effectiveScale = calibrating
    ? (computedScale ?? calibStartScale)   // live preview while calibrating
    : (type.modelScale ?? 1);              // backend-saved scale in normal mode

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (calibrating) exitCalib();
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [calibrating, onClose]);

  function enterCalib() {
    setCalibStartScale(type.modelScale ?? 1);
    setCalibPts([]);
    setRealDist("");
    setCalibSaved(false);
    setCalibrating(true);
  }

  function exitCalib() {
    setCalibrating(false);
    setCalibPts([]);
    setRealDist("");
  }

  async function applyScale() {
    if (!computedScale) return;
    try {
      await api.updateAssetType(siteId, type.typeCode, { modelScale: computedScale });
      onUploaded(); // invalidates query → parent re-fetches type with new modelScale
      setCalibSaved(true);
      setCalibrating(false);
      setCalibPts([]);
      setRealDist("");
    } catch { /* ignore */ }
  }

  const handleFile = async (file: File) => {
    setUploadStatus("uploading"); setProgress(0); setUploadError("");
    try {
      await uploadModel(siteId, type.typeCode, file, (p) => setProgress(p));
      setUploadStatus("done"); setProgress(100); onUploaded();
    } catch (e: unknown) {
      setUploadStatus("error");
      setUploadError(e instanceof Error ? e.message : t("assetsAdmin.models.unavailable"));
    }
  };

  const statusMsg = calibPts.length === 0
    ? t("assetsAdmin.models.calibPick1")
    : calibPts.length === 1
    ? t("assetsAdmin.models.calibPick2")
    : t("assetsAdmin.models.calibDistLabel");

  return (
    <div className={css.dialogOverlay} onClick={!calibrating ? onClose : undefined}>
      <div className={css.dialogCard} onClick={(e) => e.stopPropagation()}>
        <div className={css.dialogHeader}>
          <h3 className={css.dialogTitle}>
            {type.label} <span className={css.dialogCode}>({type.typeCode})</span>
          </h3>
          {!calibrating && <button className={css.dialogClose} onClick={onClose}>×</button>}
        </div>

        {/* 3D preview */}
        {type.defaultModelUrl
          ? <ModelPreview
              key={type.defaultModelUrl}
              url={type.defaultModelUrl}
              unavailableText={t("assetsAdmin.models.unavailable")}
              calibrating={calibrating}
              calibPts={calibPts}
              onPointPick={(p) => setCalibPts((prev) => prev.length < 2 ? [...prev, p] : prev)}
              effectiveScale={effectiveScale}
            />
          : <div className={css.previewEmpty}>{t("assetsAdmin.models.noModel")}</div>
        }

        {/* Calibration panel */}
        {calibrating ? (
          <div className={css.calibSection}>
            <div className={css.calibStatus}>
              <span className={`${css.calibDot}${calibPts.length >= 1 ? " " + css.calibDotDone : ""}`} />
              {statusMsg}
            </div>

            {calibPts.length === 2 && (
              <>
                <div className={css.calibRow}>
                  <label className={css.calibLabel}>{t("assetsAdmin.models.calibDistLabel")}:</label>
                  <input
                    className={css.calibInput}
                    type="number" min="0.01" step="0.01"
                    value={realDist}
                    onChange={(e) => setRealDist(e.target.value)}
                    placeholder="ex: 2.50"
                    autoFocus
                  />
                </div>
                {computedScale !== null && (
                  <span className={css.calibScaleInfo}>
                    {t("assetsAdmin.models.calibScaleInfo", { scale: computedScale.toFixed(4) })}
                  </span>
                )}
              </>
            )}

            <div className={css.calibBtnRow}>
              <button className={css.calibApplyBtn} disabled={!computedScale} onClick={applyScale}>
                {t("assetsAdmin.models.calibApply")}
              </button>
              {calibPts.length > 0 && (
                <button className={css.calibSecondaryBtn}
                  onClick={() => { setCalibPts([]); setRealDist(""); }}>
                  {t("assetsAdmin.models.calibReset")}
                </button>
              )}
              <button className={css.calibSecondaryBtn} onClick={exitCalib}>
                {t("assetsAdmin.models.calibCancelMode")}
              </button>
            </div>
          </div>
        ) : (
          <>
            {type.defaultModelUrl && (
              <span className={css.cardUrl}>{type.defaultModelUrl.split("/").pop()}</span>
            )}

            {/* Calibrate button — only when a model is loaded */}
            {type.defaultModelUrl && (
              <button className={css.calibrateBtn} onClick={enterCalib}>
                {t("assetsAdmin.models.calibrate")}
                {type.modelScale && type.modelScale !== 1
                  ? ` (${type.modelScale.toFixed(3)}×)`
                  : ""}
              </button>
            )}

            <input ref={inputRef} type="file" accept=".glb" className={css.fileInput}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            <button className={css.pickBtn} disabled={uploadStatus === "uploading"}
              onClick={() => inputRef.current?.click()}>
              {uploadStatus === "uploading" ? t("assetsAdmin.models.uploading") : t("assetsAdmin.models.upload")}
            </button>

            {uploadStatus === "uploading" && (
              <div className={css.progress}><div className={css.progressBar} style={{ width: `${progress}%` }} /></div>
            )}
            {uploadStatus === "done"  && <p className={css.successMsg}>{t("assetsAdmin.models.success")}</p>}
            {uploadStatus === "error" && <p className={css.errorMsg}>{uploadError}</p>}
            {calibSaved && <p className={css.successMsg}>{t("assetsAdmin.models.calibSaved")}</p>}
          </>
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export function AssetsAdminPage() {
  const SITE_ID = useSiteId();
  const qc = useQueryClient();
  const { t } = useTranslation();

  const { data: types = [] } = useQuery({
    queryKey: ["asset-types", SITE_ID],
    queryFn: () => api.listAssetTypes(SITE_ID),
  });

  // ── Model dialog state ─────────────────────────────────────────────────
  const [modelDialogType, setModelDialogType] = useState<AssetType | null>(null);

  // ── Asset Types CRUD ───────────────────────────────────────────────────
  const [editingTypeCode, setEditingTypeCode] = useState<string | null>(null);
  const [addingType, setAddingType] = useState(false);
  const [confirmDeleteType, setConfirmDeleteType] = useState<string | null>(null);
  const [typeError, setTypeError] = useState("");

  const createTypeMut = useMutation({
    mutationFn: (v: { typeCode: string; label: string; description?: string }) =>
      api.createAssetType(SITE_ID, v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["asset-types", SITE_ID] }); setAddingType(false); setTypeError(""); },
    onError: () => setTypeError(t("assetsAdmin.types.error.create")),
  });

  const updateTypeMut = useMutation({
    mutationFn: ({ typeCode, ...v }: { typeCode: string; label: string; description?: string }) =>
      api.updateAssetType(SITE_ID, typeCode, v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["asset-types", SITE_ID] }); setEditingTypeCode(null); setTypeError(""); },
    onError: () => setTypeError(t("assetsAdmin.types.error.update")),
  });

  const deleteTypeMut = useMutation({
    mutationFn: (typeCode: string) => api.deleteAssetType(SITE_ID, typeCode),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["asset-types", SITE_ID] }); setConfirmDeleteType(null); setTypeError(""); },
    onError: () => setTypeError(t("assetsAdmin.types.error.inUse")),
  });

  // ── Assets CRUD ────────────────────────────────────────────────────────
  const { data: assets = [] } = useQuery({
    queryKey: ["fleet", SITE_ID],
    queryFn: () => api.listAssets(SITE_ID),
  });

  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [addingAsset, setAddingAsset] = useState(false);
  const [confirmDeleteAsset, setConfirmDeleteAsset] = useState<string | null>(null);
  const [assetError, setAssetError] = useState("");
  const [edgeAssetId, setEdgeAssetId] = useState<string | null>(null);

  // Explicit-warehouse rule (retrospective C5): pass the store's warehouse when
  // selected; otherwise omit it and let the server resolve the site's single
  // warehouse (it rejects when the site has several).
  const WAREHOUSE_ID = useWarehouseId();
  const createAssetMut = useMutation({
    mutationFn: (a: { label: string; typeCode: string; model?: string; gatewayBaseUrl?: string }) =>
      api.createAsset(SITE_ID, { ...a, ...(WAREHOUSE_ID ? { warehouseId: WAREHOUSE_ID } : {}) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["fleet", SITE_ID] }); setAddingAsset(false); setAssetError(""); },
    onError: () => setAssetError(t("assetsAdmin.assets.error.create")),
  });

  const updateAssetMut = useMutation({
    mutationFn: ({ id, label, model, typeCode, modelUrl, active, gatewayBaseUrl, commandTransport, edgeConfig }: {
      id: string;
      label?: string; model?: string; typeCode?: string; modelUrl?: string; active?: boolean; gatewayBaseUrl?: string;
      commandTransport?: string; edgeConfig?: unknown;
    }) => api.updateAsset(SITE_ID, id, { label, model, typeCode, modelUrl, active, gatewayBaseUrl, commandTransport, edgeConfig }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["fleet", SITE_ID] }); setEditingAssetId(null); setEdgeAssetId(null); setAssetError(""); },
    onError: () => setAssetError(t("assetsAdmin.assets.error.update")),
  });

  const deleteAssetMut = useMutation({
    mutationFn: (id: string) => api.deleteAsset(SITE_ID, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["fleet", SITE_ID] }); setConfirmDeleteAsset(null); setAssetError(""); },
    onError: () => setAssetError(t("assetsAdmin.assets.error.delete")),
  });

  // Keep dialog in sync when the type list refreshes (e.g. after upload)
  const currentDialogType = modelDialogType
    ? (types.find((tp) => tp.typeCode === modelDialogType.typeCode) ?? modelDialogType)
    : null;

  return (
    <div className={css.root}>
      <h1 className={css.title}>{t("assetsAdmin.title")}</h1>

      {/* ── Asset Types ───────────────────────────────────────────────────── */}
      <section className={css.section}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>{t("assetsAdmin.types.section")}</h2>
          <button className={css.addBtn} onClick={() => { setAddingType(true); setEditingTypeCode(null); setTypeError(""); }}>
            {t("assetsAdmin.types.add")}
          </button>
        </div>
        {typeError && <p className={css.errorMsg}>{typeError}</p>}
        <table className={css.table}>
          <thead>
            <tr>
              <th>{t("assetsAdmin.types.col.code")}</th>
              <th>{t("common.col.label")}</th>
              <th>{t("assetsAdmin.types.col.description")}</th>
              <th>{t("assetsAdmin.types.col.defaultModel")}</th>
              <th>{t("common.col.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {addingType && (
              <AddTypeRow saving={createTypeMut.isPending}
                onSave={(v) => createTypeMut.mutate(v)}
                onCancel={() => setAddingType(false)} />
            )}
            {types.map((tp) =>
              editingTypeCode === tp.typeCode ? (
                <EditTypeRow key={tp.typeCode} type={tp} saving={updateTypeMut.isPending}
                  onSave={(upd) => updateTypeMut.mutate({ typeCode: tp.typeCode, ...upd })}
                  onCancel={() => setEditingTypeCode(null)} />
              ) : confirmDeleteType === tp.typeCode ? (
                <ConfirmDeleteRow key={tp.typeCode} colSpan={5}
                  label={t("assetsAdmin.types.confirmDelete", { code: tp.typeCode })}
                  saving={deleteTypeMut.isPending}
                  onConfirm={() => deleteTypeMut.mutate(tp.typeCode)}
                  onCancel={() => setConfirmDeleteType(null)} />
              ) : (
                <tr key={tp.typeCode}>
                  <td className={css.mono}>{tp.typeCode}</td>
                  <td>{tp.label}</td>
                  <td className={css.muted}>{tp.description ?? "—"}</td>
                  <td className={css.mono}>{tp.defaultModelUrl ? tp.defaultModelUrl.split("/").pop() : "—"}</td>
                  <td>
                    <button className={css.actionBtn} onClick={() => setModelDialogType(tp)}>{t("assetsAdmin.types.viewModel")}</button>
                    <button className={css.actionBtn} onClick={() => { setEditingTypeCode(tp.typeCode); setAddingType(false); }}>{t("common.edit")}</button>
                    <button className={`${css.actionBtn} ${css.dangerBtn}`} onClick={() => setConfirmDeleteType(tp.typeCode)}>{t("common.delete")}</button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </section>

      {/* ── Assets ────────────────────────────────────────────────────────── */}
      <section className={css.section}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>{t("assetsAdmin.assets.section")}</h2>
          <button className={css.addBtn} onClick={() => { setAddingAsset(true); setEditingAssetId(null); setAssetError(""); }}>
            {t("assetsAdmin.assets.add")}
          </button>
        </div>
        {assetError && <p className={css.errorMsg}>{assetError}</p>}
        <table className={css.table}>
          <thead>
            <tr>
              <th>{t("assetsAdmin.assets.col.id")}</th>
              <th>{t("common.col.label")}</th>
              <th>{t("assetsAdmin.assets.col.physicalModel")}</th>
              <th>{t("assetsAdmin.assets.col.type")}</th>
              <th>{t("assetsAdmin.assets.col.gateway")}</th>
              <th>{t("assetsAdmin.assets.col.active")}</th>
              <th>{t("common.col.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {addingAsset && (
              <AddAssetRow types={types} saving={createAssetMut.isPending}
                onSave={(a) => createAssetMut.mutate(a)}
                onCancel={() => setAddingAsset(false)} />
            )}
            {assets.map((asset) =>
              editingAssetId === asset.id ? (
                <EditAssetRow key={asset.id} asset={asset} types={types} saving={updateAssetMut.isPending}
                  onSave={(upd) => updateAssetMut.mutate({ id: asset.id, ...upd })}
                  onCancel={() => setEditingAssetId(null)} />
              ) : confirmDeleteAsset === asset.id ? (
                <ConfirmDeleteRow key={asset.id} colSpan={7}
                  label={t("assetsAdmin.assets.confirmDelete", { id: asset.id })}
                  saving={deleteAssetMut.isPending}
                  onConfirm={() => deleteAssetMut.mutate(asset.id)}
                  onCancel={() => setConfirmDeleteAsset(null)} />
              ) : (
                <tr key={asset.id}>
                  <td className={css.mono}>{asset.id}</td>
                  <td>{asset.label}</td>
                  <td className={css.muted}>{asset.model ?? "—"}</td>
                  <td className={css.mono}>{asset.typeCode ?? "—"}</td>
                  <td className={css.mono}>
                    {/* Binding state first: the Edge button is always offered (improvement01 —
                        transport/config precede approval), so the column must say whether a
                        real gateway is actually attached. */}
                    <span className={asset.edgeBound ? css.edgeBoundTag : css.edgeUnboundTag}>
                      {asset.edgeBound ? t("assetsAdmin.assets.edgeBound") : t("assetsAdmin.assets.edgeUnbound")}
                    </span>
                    {asset.gatewayBaseUrl ? <span className={css.muted}> {asset.gatewayBaseUrl}</span> : null}
                  </td>
                  <td>
                    <button
                      className={`${css.toggleBtn} ${asset.active ? css.activeOn : css.activeOff}`}
                      onClick={() => updateAssetMut.mutate({ id: asset.id, active: !asset.active })}
                      disabled={updateAssetMut.isPending}>
                      {asset.active ? t("common.yes") : t("common.no")}
                    </button>
                  </td>
                  <td>
                    {/* Always available — an MQTT-only edge has no gateway URL but still needs
                        its transport + edge config set (improvement01). */}
                    <button className={css.actionBtn} onClick={() => setEdgeAssetId(asset.id)}>{t("assetsAdmin.edge.button")}</button>
                    <button className={css.actionBtn} onClick={() => { setEditingAssetId(asset.id); setAddingAsset(false); }}>{t("common.edit")}</button>
                    <button className={`${css.actionBtn} ${css.dangerBtn}`} onClick={() => setConfirmDeleteAsset(asset.id)}>{t("common.delete")}</button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </section>

      {/* ── Model dialog ────────────────────────────────────────────────── */}
      {currentDialogType && (
        <ModelDialog
          type={currentDialogType}
          siteId={SITE_ID}
          onClose={() => setModelDialogType(null)}
          onUploaded={() => qc.invalidateQueries({ queryKey: ["asset-types", SITE_ID] })}
        />
      )}

      {/* ── Edge config dialog (transport + edge_config jsonb) ───────────── */}
      {edgeAssetId && (() => {
        const a = assets.find((x) => x.id === edgeAssetId);
        return a ? (
          <EdgeConfigDialog asset={a} siteId={SITE_ID} saving={updateAssetMut.isPending}
            onSave={(upd) => updateAssetMut.mutate({ id: a.id, ...upd })}
            onClose={() => setEdgeAssetId(null)} />
        ) : null;
      })()}
    </div>
  );
}

/** Per-asset edge configuration: command transport (http|mqtt) + the server-owned
 *  edge_config jsonb overlay. The edge applies changes on its next poll (~30s,
 *  no restart; decisions.md#68). A newborn edge with no per-asset config is pre-filled from
 *  the site default template, which can also be edited here (improvement01/02, decisions.md#80). */
function EdgeConfigDialog({ asset, siteId, onSave, onClose, saving }: {
  asset: Asset;
  siteId: string;
  onSave: (upd: { commandTransport: string; edgeConfig: unknown }) => void;
  onClose: () => void; saving?: boolean;
}) {
  const { t } = useTranslation();
  const [transport, setTransport] = useState(asset.commandTransport ?? "mqtt");
  const hasOwn = !!asset.edgeConfig && Object.keys(asset.edgeConfig).length > 0;
  const [json, setJson] = useState(() => hasOwn ? JSON.stringify(asset.edgeConfig, null, 2) : "");
  // "" until resolved: "asset" = this asset's own config, else where the prefill came from.
  const [source, setSource] = useState<"asset" | "site" | "builtin" | "">(hasOwn ? "asset" : "");
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  // Newborn edge (no per-asset config): pre-fill from the site default so the operator starts
  // from working values instead of an empty {} (improvement02).
  useEffect(() => {
    if (hasOwn) return;
    let alive = true;
    getEdgeConfigDefault(siteId)
      .then((d) => { if (!alive) return; setJson(JSON.stringify(d.template, null, 2)); setSource(d.source); })
      .catch(() => { if (alive) setJson("{}"); });
    return () => { alive = false; };
  }, [siteId, hasOwn]);

  // Parse the editor into a plain JSON object, or null if it isn't one.
  const parseEditor = (): Record<string, unknown> | null => {
    const txt = json.trim();
    if (!txt) return {};
    try {
      const v: unknown = JSON.parse(txt);
      if (typeof v !== "object" || v === null || Array.isArray(v)) return null;
      return v as Record<string, unknown>;
    } catch { return null; }
  };

  const save = () => {
    const ec = parseEditor();
    if (ec === null) { setErr(t("assetsAdmin.edge.invalidJson")); return; }
    setErr("");
    onSave({ commandTransport: transport, edgeConfig: ec });
  };

  const loadSiteDefault = async () => {
    try {
      const d = await getEdgeConfigDefault(siteId);
      setJson(JSON.stringify(d.template, null, 2));
      setSource(d.source); setErr(""); setNote("");
    } catch { setErr(t("assetsAdmin.edge.defaultError")); }
  };

  const saveAsSiteDefault = async () => {
    const ec = parseEditor();
    if (ec === null) { setErr(t("assetsAdmin.edge.invalidJson")); return; }
    try {
      await setEdgeConfigDefault(siteId, ec);
      setErr(""); setNote(t("assetsAdmin.edge.defaultSaved")); setSource("site");
    } catch { setErr(t("assetsAdmin.edge.defaultError")); }
  };

  const prefillNote = source === "site" ? t("assetsAdmin.edge.prefillSite")
    : source === "builtin" ? t("assetsAdmin.edge.prefillBuiltin") : "";
  const httpNeedsGateway = transport === "http" && !asset.gatewayBaseUrl;

  return (
    <div className={css.dialogOverlay} onClick={onClose}>
      <div className={css.dialogCard} onClick={(e) => e.stopPropagation()}>
        <div className={css.dialogHeader}>
          <h3 className={css.dialogTitle}>
            {t("assetsAdmin.edge.title")} <span className={css.dialogCode}>{asset.id}</span>
          </h3>
          <button className={css.dialogClose} onClick={onClose} aria-label={t("a11y.close")}>×</button>
        </div>

        <div className={css.calibRow}>
          <span className={css.calibLabel}>{t("assetsAdmin.edge.transport")}</span>
          <div className={css.calibBtnRow}>
            {(["http", "mqtt"] as const).map((tp) => (
              <button key={tp} type="button"
                className={transport === tp ? css.calibApplyBtn : css.calibSecondaryBtn}
                onClick={() => setTransport(tp)}>{tp.toUpperCase()}</button>
            ))}
          </div>
        </div>
        <p className={css.cardUrl}>{t("assetsAdmin.edge.transportHint")}</p>
        {httpNeedsGateway && <p className={css.errorMsg}>{t("assetsAdmin.edge.httpNeedsGateway")}</p>}

        <label className={css.calibLabel} htmlFor="edge-config-json">{t("assetsAdmin.edge.config")}</label>
        {prefillNote && <p className={css.cardUrl}>{prefillNote}</p>}
        <textarea id="edge-config-json" className={css.input} spellCheck={false}
          style={{ minHeight: 200, fontFamily: "var(--font-mono)", resize: "vertical" }}
          value={json} onChange={(e) => { setJson(e.target.value); setNote(""); }} />
        {err && <p className={css.errorMsg}>{err}</p>}
        {note && <p className={css.successMsg}>{note}</p>}

        <div className={css.calibBtnRow}>
          <button className={css.pickBtn} disabled={saving} onClick={save}>{t("common.save")}</button>
          <button className={css.calibSecondaryBtn} onClick={loadSiteDefault}>{t("assetsAdmin.edge.loadDefault")}</button>
          <button className={css.calibSecondaryBtn} onClick={saveAsSiteDefault}>{t("assetsAdmin.edge.saveAsDefault")}</button>
          <button className={css.calibSecondaryBtn} onClick={onClose}>{t("common.cancel")}</button>
        </div>
      </div>
    </div>
  );
}

// ── Inline form sub-components ─────────────────────────────────────────────

function AddTypeRow({ onSave, onCancel, saving }: {
  onSave: (v: { typeCode: string; label: string; description?: string }) => void;
  onCancel: () => void; saving?: boolean;
}) {
  const { t } = useTranslation();
  const [typeCode, setTypeCode] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  return (
    <tr>
      <td><input className={css.input} value={typeCode} onChange={e => setTypeCode(e.target.value)} placeholder={t("common.placeholder.code")} /></td>
      <td><input className={css.input} value={label} onChange={e => setLabel(e.target.value)} placeholder={t("common.placeholder.label")} /></td>
      <td><input className={css.input} value={description} onChange={e => setDescription(e.target.value)} placeholder={t("common.placeholder.description")} /></td>
      <td>—</td>
      <td>
        <button className={css.actionBtn} disabled={saving} onClick={() => onSave({ typeCode, label, description: description || undefined })}>{t("common.save")}</button>
        <button className={css.actionBtn} onClick={onCancel}>{t("common.cancel")}</button>
      </td>
    </tr>
  );
}

function EditTypeRow({ type, onSave, onCancel, saving }: {
  type: AssetType;
  onSave: (v: { label: string; description?: string }) => void;
  onCancel: () => void; saving?: boolean;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(type.label);
  const [description, setDescription] = useState(type.description ?? "");
  return (
    <tr>
      <td className={css.mono}>{type.typeCode}</td>
      <td><input className={css.input} value={label} onChange={e => setLabel(e.target.value)} /></td>
      <td><input className={css.input} value={description} onChange={e => setDescription(e.target.value)} /></td>
      <td className={css.mono}>{type.defaultModelUrl ? type.defaultModelUrl.split("/").pop() : "—"}</td>
      <td>
        <button className={css.actionBtn} disabled={saving} onClick={() => onSave({ label, description: description || undefined })}>{t("common.save")}</button>
        <button className={css.actionBtn} onClick={onCancel}>{t("common.cancel")}</button>
      </td>
    </tr>
  );
}

function AddAssetRow({ types, onSave, onCancel, saving }: {
  types: AssetType[];
  onSave: (a: { label: string; typeCode: string; model?: string; gatewayBaseUrl?: string }) => void;
  onCancel: () => void; saving?: boolean;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState("");
  const [typeCode, setTypeCode] = useState(types[0]?.typeCode ?? "");
  const [model, setModel] = useState("");
  const [gatewayBaseUrl, setGatewayBaseUrl] = useState("");
  return (
    <tr>
      <td>—</td>
      <td><input className={css.input} value={label} onChange={e => setLabel(e.target.value)} placeholder={t("common.placeholder.label")} /></td>
      <td><input className={css.input} value={model} onChange={e => setModel(e.target.value)} placeholder={t("common.placeholder.physicalModel")} /></td>
      <td>
        <select className={css.select} value={typeCode} onChange={e => setTypeCode(e.target.value)}>
          {types.map(tp => <option key={tp.typeCode} value={tp.typeCode}>{tp.typeCode} — {tp.label}</option>)}
        </select>
      </td>
      <td><input className={css.input} value={gatewayBaseUrl} onChange={e => setGatewayBaseUrl(e.target.value)} placeholder={t("assetsAdmin.assets.gatewayPlaceholder")} /></td>
      <td>—</td>
      <td>
        <button className={css.actionBtn} disabled={saving} onClick={() => onSave({ label, typeCode, model: model || undefined, gatewayBaseUrl: gatewayBaseUrl.trim() || undefined })}>{t("common.save")}</button>
        <button className={css.actionBtn} onClick={onCancel}>{t("common.cancel")}</button>
      </td>
    </tr>
  );
}

function EditAssetRow({ asset, types, onSave, onCancel, saving }: {
  asset: Asset; types: AssetType[];
  onSave: (p: { label?: string; model?: string; typeCode?: string; gatewayBaseUrl?: string }) => void;
  onCancel: () => void; saving?: boolean;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(asset.label);
  const [model, setModel] = useState(asset.model ?? "");
  const [typeCode, setTypeCode] = useState(asset.typeCode ?? "");
  const [gatewayBaseUrl, setGatewayBaseUrl] = useState(asset.gatewayBaseUrl ?? "");
  return (
    <tr>
      <td className={css.mono}>{asset.id}</td>
      <td><input className={css.input} value={label} onChange={e => setLabel(e.target.value)} /></td>
      <td><input className={css.input} value={model} onChange={e => setModel(e.target.value)} /></td>
      <td>
        <select className={css.select} value={typeCode} onChange={e => setTypeCode(e.target.value)}>
          {types.map(tp => <option key={tp.typeCode} value={tp.typeCode}>{tp.typeCode} — {tp.label}</option>)}
        </select>
      </td>
      <td><input className={css.input} value={gatewayBaseUrl} onChange={e => setGatewayBaseUrl(e.target.value)} placeholder={t("assetsAdmin.assets.gatewayPlaceholder")} /></td>
      <td>—</td>
      <td>
        <button className={css.actionBtn} disabled={saving} onClick={() => onSave({ label, model: model || undefined, typeCode, gatewayBaseUrl: gatewayBaseUrl.trim() })}>{t("common.save")}</button>
        <button className={css.actionBtn} onClick={onCancel}>{t("common.cancel")}</button>
      </td>
    </tr>
  );
}

function ConfirmDeleteRow({ colSpan, label, onConfirm, onCancel, saving }: {
  colSpan: number; label: string;
  onConfirm: () => void; onCancel: () => void; saving?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <tr>
      <td colSpan={colSpan}>
        <span className={css.confirmText}>{label}</span>
        <button className={`${css.actionBtn} ${css.dangerBtn}`} disabled={saving} onClick={onConfirm}>{t("common.confirm")}</button>
        <button className={css.actionBtn} onClick={onCancel}>{t("common.cancel")}</button>
      </td>
    </tr>
  );
}
